import { useState, Fragment } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "../../lib/trpc.js";
import { fmt } from "./finance-utils.js";
import { OpeningBalancesModal } from "./OpeningBalancesModal.js";
import { useFinanceCanEdit } from "./finance-access.js";
import { useT } from "../../i18n/index.js";

const ACCOUNTS = ["Neto", "Landione", "Despesas", "Pagar Técnico"] as const;

export function FechamentoPage() {
  const t = useT();
  const { data: rows = [], isLoading } = trpc.finance.dashboard.monthly.useQuery();
  const { data: cfg } = trpc.finance.settings.get.useQuery();
  const currency = cfg?.currency === "BRL" ? "R$" : cfg?.currency === "USD" ? "$" : "€";
  const f = (v: number) => fmt(v, currency);
  const [range, setRange] = useState(12);
  const canEdit = useFinanceCanEdit();
  const { data: opening = [] } = trpc.finance.openingBalances.list.useQuery();
  const openingMap: Record<string, number> = Object.fromEntries(
    opening.map((o) => [o.account, o.amount]),
  );
  const lastEdit = opening
    .filter((o) => o.updatedAt)
    .sort((a, b) => String(b.updatedAt).localeCompare(String(a.updatedAt)))[0];
  const [editOpening, setEditOpening] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (m: string) =>
    setExpanded((prev) => {
      const s = new Set(prev);
      s.has(m) ? s.delete(m) : s.add(m);
      return s;
    });

  if (isLoading)
    return <div style={{ color: "var(--text3)", fontSize: 13 }}>{t("financeClosing.loading")}</div>;

  const MESES = [
    t("financeClosing.monthJan"),
    t("financeClosing.monthFeb"),
    t("financeClosing.monthMar"),
    t("financeClosing.monthApr"),
    t("financeClosing.monthMay"),
    t("financeClosing.monthJun"),
    t("financeClosing.monthJul"),
    t("financeClosing.monthAug"),
    t("financeClosing.monthSep"),
    t("financeClosing.monthOct"),
    t("financeClosing.monthNov"),
    t("financeClosing.monthDec"),
  ];

  const RANGES = [
    { v: 3, l: t("financeClosing.range3Months") },
    { v: 6, l: t("financeClosing.range6Months") },
    { v: 12, l: t("financeClosing.range12Months") },
    { v: 0, l: t("financeClosing.rangeAll") },
  ];

  const monthLabel = (m: string) => {
    const [y, mo] = m.split("-");
    return `${MESES[Number(mo) - 1]}/${(y ?? "").slice(2)}`;
  };
  const shown = range === 0 ? rows : rows.slice(-range);
  const colorOf = (v: number) => (v > 0 ? "var(--green)" : v < 0 ? "var(--red)" : "var(--text3)");

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
          flexWrap: "wrap",
          gap: 10,
        }}
      >
        <div className="section-title" style={{ margin: 0 }}>
          {t("financeClosing.title")}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {RANGES.map((o) => (
            <button
              key={o.v}
              className={`btn${range === o.v ? " primary" : ""}`}
              style={{ fontSize: 12, padding: "5px 12px" }}
              onClick={() => setRange(o.v)}
            >
              {o.l}
            </button>
          ))}
        </div>
      </div>

      {/* Saldos de abertura (editável com confirmação + audit) */}
      <div
        style={{
          background: "var(--bg2)",
          border: "1px solid var(--border)",
          borderRadius: "var(--r2)",
          padding: "14px 16px",
          marginBottom: 20,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 10,
          }}
        >
          <div className="section-title" style={{ margin: 0, fontSize: 12 }}>
            {t("financeClosing.openingBalances")}
          </div>
          {canEdit && (
            <button
              className="btn"
              style={{ fontSize: 11, padding: "4px 10px" }}
              onClick={() => setEditOpening(true)}
            >
              {t("financeClosing.edit")}
            </button>
          )}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {ACCOUNTS.map((a) => (
            <div
              key={a}
              style={{
                background: "var(--bg3)",
                border: "1px solid var(--border)",
                borderRadius: "var(--r)",
                padding: "6px 12px",
                fontSize: 12,
              }}
            >
              <span style={{ color: "var(--text3)" }}>{a}: </span>
              <span style={{ fontFamily: "var(--mono)" }}>{f(openingMap[a] ?? 0)}</span>
            </div>
          ))}
        </div>
        {lastEdit?.updatedAt && (
          <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 8 }}>
            {t("financeClosing.updatedOn")}{" "}
            {new Date(lastEdit.updatedAt).toLocaleDateString("pt-BR")}
            {lastEdit.editor ? " " + t("financeClosing.by") + " " + lastEdit.editor : ""}
          </div>
        )}
      </div>

      {shown.length === 0 ? (
        <div className="empty-state">
          <div className="icon">📅</div>
          <p>
            {t("financeClosing.emptyTitle")}
            <br />
            {t("financeClosing.emptyHint")}
          </p>
        </div>
      ) : (
        <>
          {/* Resumo consolidado */}
          <div className="section-title" style={{ fontSize: 12 }}>
            {t("financeClosing.consolidatedSummary")}
          </div>
          <div className="table-wrap" style={{ marginBottom: 24 }}>
            <table>
              <thead>
                <tr>
                  <th>{t("financeClosing.colMonth")}</th>
                  <th style={{ textAlign: "right" }}>{t("financeClosing.colInflows")}</th>
                  <th style={{ textAlign: "right" }}>{t("financeClosing.colOutflows")}</th>
                  <th style={{ textAlign: "right" }}>{t("financeClosing.colResult")}</th>
                  <th style={{ textAlign: "right" }}>{t("financeClosing.colClosingBalance")}</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <Fragment key={r.month}>
                    <tr onClick={() => toggle(r.month)} style={{ cursor: "pointer" }}>
                      <td className="mono" style={{ whiteSpace: "nowrap" }}>
                        {expanded.has(r.month) ? "▾ " : "▸ "}
                        {monthLabel(r.month)}
                      </td>
                      <td className="mono" style={{ textAlign: "right", color: "var(--green)" }}>
                        {r.entradas > 0 ? f(r.entradas) : "—"}
                      </td>
                      <td className="mono" style={{ textAlign: "right", color: "var(--red)" }}>
                        {r.saidas > 0 ? f(r.saidas) : "—"}
                      </td>
                      <td
                        className="mono"
                        style={{
                          textAlign: "right",
                          color: r.resultado >= 0 ? "var(--green)" : "var(--red)",
                        }}
                      >
                        {f(r.resultado)}
                      </td>
                      <td
                        className="mono"
                        style={{
                          textAlign: "right",
                          fontWeight: 500,
                          color: r.saldoFinal >= 0 ? "var(--text)" : "var(--red)",
                        }}
                      >
                        {f(r.saldoFinal)}
                      </td>
                    </tr>
                    {expanded.has(r.month) && (
                      <tr>
                        <td colSpan={5} style={{ background: "var(--bg3)", padding: "10px 16px" }}>
                          <div
                            style={{
                              fontSize: 11,
                              color: "var(--text3)",
                              marginBottom: 8,
                              textTransform: "uppercase",
                              letterSpacing: ".06em",
                            }}
                          >
                            {t("financeClosing.monthMovementByAccount")}
                          </div>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))",
                              gap: 8,
                            }}
                          >
                            {ACCOUNTS.map((a) => {
                              const m = r.movimento[a];
                              if (!m || (m.in === 0 && m.out === 0)) return null;
                              return (
                                <div
                                  key={a}
                                  style={{
                                    fontSize: 12,
                                    display: "flex",
                                    justifyContent: "space-between",
                                    gap: 8,
                                    background: "var(--bg2)",
                                    borderRadius: 4,
                                    padding: "5px 9px",
                                  }}
                                >
                                  <span style={{ color: "var(--text2)" }}>{a}</span>
                                  <span style={{ fontFamily: "var(--mono)" }}>
                                    {m.in > 0 && (
                                      <span style={{ color: "var(--green)" }}>+{f(m.in)}</span>
                                    )}
                                    {m.in > 0 && m.out > 0 ? " " : ""}
                                    {m.out > 0 && (
                                      <span style={{ color: "var(--red)" }}>−{f(m.out)}</span>
                                    )}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Saldo final por conta */}
          <div className="section-title" style={{ fontSize: 12 }}>
            {t("financeClosing.closingBalanceByAccount")}
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t("financeClosing.colMonth")}</th>
                  {ACCOUNTS.map((a) => (
                    <th key={a} style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      {a}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.month}>
                    <td className="mono" style={{ whiteSpace: "nowrap" }}>
                      {monthLabel(r.month)}
                    </td>
                    {ACCOUNTS.map((a) => {
                      const v = r.contas[a] ?? 0;
                      return (
                        <td
                          key={a}
                          className="mono"
                          style={{ textAlign: "right", color: colorOf(v) }}
                        >
                          {v !== 0 ? f(v) : "—"}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 12, lineHeight: 1.6 }}>
            {t("financeClosing.footnote")}
          </div>
        </>
      )}

      {editOpening && (
        <OpeningBalancesModal initial={openingMap} onClose={() => setEditOpening(false)} />
      )}
    </div>
  );
}
